import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useDropzone } from 'react-dropzone'
import { CloudUploadIcon, XCircleIcon } from '@heroicons/react/solid'

import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'

const MAX_SIZE = 8 * 1024 * 1024
const ACCEPTED = 'image/*'

const sanitizeBaseName = (name) =>
  name.replace(/\.[^/.]+$/, '').replace(/[^\w\d_\-.]+/ig, '').slice(0, 40) || 'image'

const FeedbackImageUploader = ({ urls, onChange, max }) => {
  const [, setToast] = useGlobalState('toast')
  const { getRequest } = useQuery()
  const csrfToken = (document.head.querySelector('[name~=csrf-token]') || {}).content
  // Items currently being uploaded. Each has a blob preview URL + an id.
  // Once uploaded, they drop out of this list and their final R2 URL goes
  // into the parent's `urls` array via onChange.
  const [pending, setPending] = useState([])

  const remainingSlots = Math.max(0, max - urls.length - pending.length)

  const showError = (msg) => {
    setToast(
      <div className='ml-3 w-0 flex-1 pt-0.5'>
        <p className='text-sm font-medium dark:text-red-100 text-red-600'>Upload error</p>
        <p className='mt-1 text-sm dark:text-red-300 text-red-400'>{msg}</p>
      </div>
    )
  }

  const uploadOne = (file, pendingId) => {
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
    const key = `feedback-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${sanitizeBaseName(file.name)}.${ext}`

    getRequest('/api/v3/image_uploads/r2_hash', { key, kind: 'Feedback', content_type: file.type }, (err, jsonData) => {
      if (err) {
        setPending((prev) => prev.filter((p) => p.id !== pendingId))
        showError(typeof err === 'string' ? err : 'Could not sign upload')
        return
      }

      window.fetch(jsonData.url, {
        method: 'PUT',
        headers: { 'X-CSRF-TOKEN': csrfToken, 'Content-Type': file.type },
        body: file
      }).then((resp) => {
        if (!resp.ok) { throw new Error(`R2 responded ${resp.status}`) }
        const finalUrl = jsonData.image_url + key
        setPending((prev) => prev.filter((p) => p.id !== pendingId))
        onChange([...urls, finalUrl])
      }).catch((e) => {
        setPending((prev) => prev.filter((p) => p.id !== pendingId))
        showError(e.message || 'Upload failed')
      })
    })
  }

  const onDrop = useCallback((accepted, rejected) => {
    if (rejected.length > 0) {
      showError('Only jpg, png, gif, webp under 8MB.')
    }
    const allowed = accepted.slice(0, remainingSlots)
    if (accepted.length > allowed.length) {
      showError(`Max ${max} images.`)
    }
    const newPending = allowed.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      preview: URL.createObjectURL(file),
      file
    }))
    setPending((prev) => [...prev, ...newPending])
    newPending.forEach((p) => uploadOne(p.file, p.id))
  }, [urls, pending.length, remainingSlots])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED,
    maxSize: MAX_SIZE,
    disabled: remainingSlots === 0
  })

  const removeUrl = (url) => {
    onChange(urls.filter((u) => u !== url))
  }

  return (
    <div>
      <div className='flex flex-wrap gap-2 mb-2'>
        {urls.map((url) => (
          <div key={url} className='relative group'>
            <img src={url} alt='' className='h-20 w-20 object-cover rounded-lg border border-gray-200 dark:border-gray-700' />
            <button
              type='button'
              onClick={() => removeUrl(url)}
              className='absolute -top-1 -right-1 bg-white dark:bg-gray-800 rounded-full shadow opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100 transition-opacity'
              aria-label='Remove image'
            >
              <XCircleIcon className='h-5 w-5 text-red-500' />
            </button>
          </div>
        ))}
        {pending.map((p) => (
          <div key={p.id} className='relative'>
            <img src={p.preview} alt='' className='h-20 w-20 object-cover rounded-lg border border-gray-200 dark:border-gray-700 opacity-50' />
            <div className='absolute inset-0 flex items-center justify-center text-xs font-medium text-white bg-black/40 rounded-lg'>
              Uploading…
            </div>
          </div>
        ))}
      </div>

      {remainingSlots > 0 && (
        <div
          {...getRootProps()}
          className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg p-6 text-sm cursor-pointer transition-colors ${
            isDragActive
              ? 'border-cccblue bg-cccblue/10 text-cccblue'
              : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-cccblue hover:text-cccblue'
          }`}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon className='h-8 w-8' />
          <span className='font-medium'>
            {isDragActive ? 'Drop images to upload' : 'Click or drag to attach screenshots'}
          </span>
          <span className='text-xs text-gray-500 dark:text-gray-400'>
            {urls.length + pending.length}/{max} — PNG, JPG, GIF, WEBP up to 8MB
          </span>
        </div>
      )}
    </div>
  )
}

FeedbackImageUploader.propTypes = {
  urls: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.number
}

FeedbackImageUploader.defaultProps = {
  max: 5
}

export default FeedbackImageUploader
